/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable max-len */
import { Injectable } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { DataCollectorService } from "../data_collector_services/data_collector_service.service";
import { LineItemService } from "../lineitem/lineitem.service";
import { AnalyticsService } from "../analytics/analytics.service";
import { TranslateService } from "@ngx-translate/core";
import { ZuperDialogService } from "@zuper/components/zuper-dialog";
import { PricelistType } from "app/modules/price-list/price-list.interface";
import { ProductType } from "app/core/constants/constants";
import { bundlePricingLevel } from "app/modules/products/products.types";
import { UtilityService } from "../utility_functions/utility_functions.service";

interface CurrentPricelistAndDetails {
  pricelistUid: string;
  pricelistType: PricelistType;
  associateDetails: any;
}

export interface PricelistAssociations {
  selectedProperty: any;
  selectedCustomer: any;
  selectedOrganization: any;
  selectedJob?: any;
  selectedProject?: any;
}

export interface PricelistAssociationsActive extends PricelistAssociations {
  pricelistUid: string;
}

@Injectable({
  providedIn: "root",
})
export class PricelistService {
  get pricelistUrl(): any{
    return `${this.dataCollectorService.baseurl()}/products/pricelist`;
  }
  get priceListApiUrls(): any{
    return {
      dataUrl: `${this.pricelistUrl}/filter`,
      columnUrl: `${this.pricelistUrl}/meta`,
      filterUrl: `${this.pricelistUrl}/meta/filter`,
      bulkActionUrl: `${this.pricelistUrl}/bulk_action`,
    };
  }

  constructor(
    private dataCollectorService: DataCollectorService,
    private lineItemService: LineItemService,
    private analytics: AnalyticsService,
    private translateService: TranslateService,
    private zuperDialogService: ZuperDialogService,
    private utilityService: UtilityService
  ) {}

  getPricelist(params): Observable<any> {
    return this.dataCollectorService.getData(this.pricelistUrl, params);
  }

  getPricelistDetails(pricelistUid: string): Observable<any> {
    const pricelistUrl = `${this.pricelistUrl}/${pricelistUid}`;
    return this.dataCollectorService.getData(pricelistUrl);
  }

  updatePricelistDetails(pricelistUid: string, payload: any): Observable<any> {
    const url = `${this.pricelistUrl}/${pricelistUid}`;
    return this.dataCollectorService.putData(url, payload);
  }

  activatePricelist(pricelistUid: string, payload: any): Observable<any> {
    const url = `${this.pricelistUrl}/${pricelistUid}/activate`;
    return this.dataCollectorService.putData(url, payload);
  }

  deletePricelist(pricelistUid: string): Observable<any> {
    const url = `${this.pricelistUrl}/${pricelistUid}`;
    return this.dataCollectorService.deleteData(url);
  }

  createPricelist(payload: any): Observable<any> {
    return this.dataCollectorService.postData(this.pricelistUrl, payload);
  }

  getProductDetails(lineItemsUidArray: any, pricelistUid): Observable<any> {
    const productPayload: any = {
      count: 0,
      "filter.product_uid": "",
      page: 1,
    };
    productPayload.count = lineItemsUidArray.length;
    productPayload["filter.product_uid"] = lineItemsUidArray.join(",");
    if (pricelistUid) {
      productPayload["pricelist"] = pricelistUid;
    } else if (pricelistUid === "") {
      delete productPayload.pricelist;
    }
    return this.dataCollectorService.getData(
      this.lineItemService.lineItemAPIUrls.productUrl,
      productPayload
    );
  }

  setMixpanelEvent(event: string): void {
    this.analytics.trackEvent(event);
  }

  updatePriceBasedOnPricelist(
    pricelistBasedLineItems,
    normalLineItem,
    pricelistUid,
    module?,
    isClearDiscount?
  ): any {
    const productUidBasedObj = Object.fromEntries(
      pricelistBasedLineItems.map((product: any) => [product.product_uid, product])
    );
    if(normalLineItem){
      normalLineItem.map((element) => {
        const product = productUidBasedObj[element?.product_uid];
        if(product){
          const isBundleRollup = element?.product_type === ProductType.BUNDLE && element?.pricing_level === bundlePricingLevel.ROLLUP && element?.associated_products?.length;

          if(isBundleRollup){
            element?.associated_products?.forEach((associatedProduct) => {
              const matchedProduct = product?.associated_products.find(associatedProd => associatedProd?.product?.product_uid === associatedProduct?.product?.product_uid);
              if(matchedProduct) {
                const associatedItemPrice = matchedProduct?.product?.price ?? matchedProduct?.product?.unit_price;
                associatedProduct.price = associatedItemPrice;
                associatedProduct.unit_price = associatedItemPrice;
              }
              if(pricelistUid){
                associatedProduct.markup = null;
                associatedProduct.discount = 0;
                associatedProduct.discount_type = 'FIXED';
              }else{
                associatedProduct.markup = matchedProduct?.markup;
                if(isClearDiscount){
                  element.discount = 0;
                  element.discount_type = 'FIXED';
                }
              }
            });
          }

          if(pricelistUid){
            element.price = product?.price;
            element.unit_price = product?.price;
            element.markup = null;
            if(module === 'SERVICE_CONTRACT'){
              element.discount = 0;
            }
          }else{
            element.markup = product?.markup;
            if(isClearDiscount){
              element.discount = 0;
              element.discount_type = 'FIXED';
            }
          }
        }
      });
    }
  }

  //<------ pricelist utility services ------>

  constructPricelistObject(details: any): any {
    let pricelistObj = {};
    if (
      (details?.pricelist?.pricelist_name ?? details?.pricelist_name) &&
     ( details?.pricelist?.pricelist_type ??  details?.pricelist_type)
    ) {
      pricelistObj["pricelist_name"] = details?.pricelist?.pricelist_name ?? details?.pricelist_name;
      pricelistObj["pricelist_type"] = details?.pricelist?.pricelist_type ??  details?.pricelist_type;
      pricelistObj["pricelist_uid"] = details?.pricelist?.pricelist_uid ?? details?.pricelist_uid;
    }
    if (
      (details?.pricelist?.pricelist_value_type ?? details?.pricelist_value_type)&&
      (details?.pricelist?.pricelist_value ?? details?.pricelist_value)
    ) {
      pricelistObj["pricelist_value_type"] =
        details?.pricelist?.pricelist_value_type ?? details?.pricelist_value_type;
      pricelistObj["pricelist_value"] = details?.pricelist?.pricelist_value ?? details?.pricelist_value;
    }
    if (Object.keys(pricelistObj)?.length === 0) {
      pricelistObj = null;
    }
    return pricelistObj;
  }

  showPricelistConfirmation(module: string): any {
    const message = `<p class="text-secondary"> ${this.translateService.instant(
      "text.pricelist.change.confirmation.message"
    )} ${module}.</p>`;
    const dialog = this.zuperDialogService.open({
      title: this.translateService.instant(
        "text.pricelist.change.confirmation.heading"
      ),
      message: message,
      type: "error",
      actions: {
        confirm: {
          show : true,
          label: 'Ok',
          color: 'primary',
          confirmDisplayKey:'proceed',
        }
      }
    });
    return dialog;
  }

  showPricelistJobConfirmation(type: string): any {
    const message =
      type === "update"
        ? `<span>${this.translateService.instant(
            "text.update.pricelist.confirmation message"
          )}</span>`
        : `<span>${this.translateService.instant(
            "text.remove.pricelist.confirmation message"
          )} ?</span>`;
    const dialog = this.zuperDialogService.open({
      title:
        type === "update"
          ? this.translateService.instant("text.update.pricelist")
          : this.translateService.instant("text.remove.pricelist"),
      message: message,
      type: "warning",
      actions: {
        confirm: {
          show : true,
          label: 'Proceed',
          color: 'primary',
          confirmDisplayKey:'proceed',
        },
        cancel: {
          show: true,
          label: "Cancel",
          cancelDisplayKey: "cancel.text",
        },
      },
    });
    return dialog;
  }

  isPricelistActive({
    selectedProperty,
    selectedCustomer,
    selectedOrganization,
    selectedJob,
    selectedProject,
  }: PricelistAssociations): boolean {
    const jobPricelistActive = selectedJob?.pricelist?.is_active ?? false;
    const propertyPricelistActive =
      selectedProperty?.pricelist?.is_active ?? false;
    const customerPricelistActive =
      selectedCustomer?.pricelist?.is_active ?? false;
    const organizationPricelistActive =
      selectedOrganization?.pricelist?.is_active ?? false;

    const noActiveJobPricelist = !selectedJob?.pricelist || !jobPricelistActive;
    const noActivePropertyPricelist =
      !selectedProperty?.pricelist || !propertyPricelistActive;
    const noActiveCustomerPricelist =
      !selectedCustomer?.pricelist || !customerPricelistActive;
    const projectPricelistActive = selectedProject?.pricelist?.is_active;
    const noActiveProjectPricelist =
      !selectedProject?.pricelist || !projectPricelistActive;

    const isActivePricelist: boolean =
      projectPricelistActive ||
      (noActiveProjectPricelist && jobPricelistActive) ||
      (noActiveJobPricelist && propertyPricelistActive) ||
      (noActiveJobPricelist &&
        noActivePropertyPricelist &&
        customerPricelistActive) ||
      (noActiveJobPricelist &&
        noActivePropertyPricelist &&
        noActiveCustomerPricelist &&
        organizationPricelistActive);
    return isActivePricelist;
  }

  isDifferentPricelist({
    selectedProperty,
    selectedCustomer,
    selectedOrganization,
    selectedJob,
    selectedProject,
    pricelistUid,
  }: PricelistAssociationsActive): boolean {
    const jobPricelistActive = selectedJob?.pricelist?.is_active ?? false;
    const propertyPricelistActive =
      selectedProperty?.pricelist?.is_active ?? false;
    const customerPricelistActive =
      selectedCustomer?.pricelist?.is_active ?? false;
    const organizationPricelistActive =
      selectedOrganization?.pricelist?.is_active ?? false;
    const projectPricelistActive =
      selectedProject?.pricelist?.is_active ?? false;

    let pricelistUidToCheck = '';
    if(projectPricelistActive){
      pricelistUidToCheck = selectedProject?.pricelist?.pricelist_uid;
    }else if (jobPricelistActive) {
      pricelistUidToCheck = selectedJob?.pricelist?.pricelist_uid;
    } else if (propertyPricelistActive) {
      pricelistUidToCheck = selectedProperty?.pricelist?.pricelist_uid;
    } else if (customerPricelistActive) {
      pricelistUidToCheck = selectedCustomer?.pricelist?.pricelist_uid;
    } else if (organizationPricelistActive) {
      pricelistUidToCheck = selectedOrganization?.pricelist?.pricelist_uid;
    }
    if(this.utilityService.isEmptyOrUndefined(pricelistUidToCheck) && this.utilityService.isEmptyOrUndefined(pricelistUid)){
      return false;
    }else{
      return pricelistUidToCheck !== pricelistUid;
    }
  }

  getCurrentPricelistAndDetails({
    selectedProperty,
    selectedCustomer,
    selectedOrganization,
    selectedJob,
    selectedProject,
  }: PricelistAssociations): CurrentPricelistAndDetails {
    let associateDetails: any;
    let pricelistUid: string = '';
    let pricelistType: PricelistType;

    const propertyPricelist: any = selectedProperty?.pricelist;
    const customerPricelist: any = selectedCustomer?.pricelist;
    const organizationPricelist: any = selectedOrganization?.pricelist;
    const projectPricelist: any = selectedProject?.pricelist;
    const jobPricelist: any = selectedJob?.pricelist;

    const jobPricelistActive: boolean = jobPricelist?.is_active ?? false;
    const propertyPricelistActive: boolean = propertyPricelist?.is_active ?? false;
    const customerPricelistActive: boolean = customerPricelist?.is_active ?? false;
    const organizationPricelistActive: boolean = organizationPricelist?.is_active ?? false;
    const projectPricelistActive: boolean = projectPricelist?.is_active ?? false;
    
    if (projectPricelistActive) {
      pricelistUid = projectPricelist?.pricelist_uid;
      pricelistType = projectPricelist?.pricelist_type;
      associateDetails = selectedProject;
    } else if (jobPricelistActive) {
      pricelistUid = jobPricelist?.pricelist_uid;
      pricelistType = jobPricelist?.pricelist_type;
      associateDetails = selectedJob;
    } else if (propertyPricelistActive) {
      pricelistUid = propertyPricelist?.pricelist_uid;
      pricelistType = propertyPricelist?.pricelist_type;
      associateDetails = selectedProperty;
    } else if (!propertyPricelistActive && customerPricelistActive) {
      pricelistUid = customerPricelist?.pricelist_uid;
      pricelistType = customerPricelist?.pricelist_type;
      associateDetails = selectedCustomer;
    } else if (
      !propertyPricelistActive &&
      !customerPricelistActive &&
      organizationPricelistActive
    ) {
      pricelistUid = organizationPricelist?.pricelist_uid;
      pricelistType = organizationPricelist?.pricelist_type;
      associateDetails = selectedOrganization;
    }
    return { pricelistUid, pricelistType, associateDetails };
  }

  constructPricelistProductUidArray(lineItems): any[] {
    const productUidArray = lineItems?.length
      ? lineItems?.map((item) => item?.product?.product_uid)
      : [];
    return productUidArray;
  }
}
